"use client";

// move to generic query params in the future
import { uniqueAndSorted } from "@faire/deprecated-retailer-visitor-shared/lib/uniqueAndSorted";
import { QueryParameters } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import { isTupleOfLength } from "@faire/web/common/typescriptUtils";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import qs from "query-string";
import { useEffect, useMemo, useRef, useCallback, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";

import { FilterablePaginatedSectionContext } from "../FilterablePaginatedSectionContext";

export const useSDUIFilterablePaginatedSectionState = (isRSC?: boolean) => {
  const { isTransitioningFunction, setIsTransitioning } = useContext(
    FilterablePaginatedSectionContext
  );
  const { pathname } = useLocation();
  const history = useHistory();
  const queryParams = useQueryParams(QueryParameters.parse);
  const updateQueryParamsAndStringify =
    useUpdateQueryParamsAndStringify(queryParams);

  const { filters } = queryParams;

  const uniqueSortedFilterKeys = useMemo(() => {
    // This is to support standard query params like `?filters=k1&filters=k2`
    const normalizedFiltersQueryParams =
      filters && isTupleOfLength(filters, 1)
        ? filters[0].split(",")
        : (filters ?? []);

    return uniqueAndSorted(normalizedFiltersQueryParams);
  }, [filters]);

  const setFilterKeysBase = useCallback(
    (filterKeys: string[]) => {
      const updatedFilterKeys = uniqueAndSorted(filterKeys);
      const queryString = updateQueryParamsAndStringify((oldParams) => ({
        ...oldParams,
        filters:
          updatedFilterKeys.length === 0
            ? undefined
            : [updatedFilterKeys.join(",")],
        page: undefined,
      }));
      history.push({ pathname, search: queryString });
    },
    [history, pathname, updateQueryParamsAndStringify]
  );

  const setFilterKeys = useCallback(
    (filterKeys: string[]) => {
      if (isRSC) {
        isTransitioningFunction?.(true);
        setIsTransitioning?.(true);
        setTimeout(() => {
          setFilterKeysBase(filterKeys);
        }, 0);
      } else {
        setFilterKeysBase(filterKeys);
      }
    },
    [isRSC, isTransitioningFunction, setIsTransitioning, setFilterKeysBase]
  );

  const oneIndexedPageNumber = queryParams.page ?? 1;

  const setOneIndexedPageNumber = useCallback(
    (updater: (oldPage: number) => number) => {
      const newPageNumber = updater(queryParams.page ?? 1);
      const queryString = updateQueryParamsAndStringify((oldParams) => ({
        ...oldParams,
        page: newPageNumber === 1 ? undefined : newPageNumber,
      }));

      history.push({ pathname, search: queryString });
    },
    [history, pathname, queryParams.page, updateQueryParamsAndStringify]
  );

  return {
    filterKeys: uniqueSortedFilterKeys,
    setFilterKeys,
    oneIndexedPageNumber,
    setOneIndexedPageNumber,
  };
};

const useUpdateQueryParamsAndStringify = (queryParams: QueryParameters) => {
  const queryParamsInMemory = useRef(queryParams);

  useEffect(() => {
    queryParamsInMemory.current = queryParams;
  }, [queryParams]);

  return (updater: (oldParams: QueryParameters) => QueryParameters): string => {
    queryParamsInMemory.current = updater(queryParamsInMemory.current);
    return qs.stringify(queryParamsInMemory.current);
  };
};
